import React from "react";
import { useDocumentMeta, useEditorUi, useViewport } from "../store/selectors";
import type { Rect } from "../types";
import { DocumentLayer } from "./DocumentLayer";
import { DraftLayer } from "./DraftLayer";
import { AutoZoneSvg, GuidePointSvg, ZoneChecksSvg } from "./EditorOverlays";
import { GridLayer } from "./GridLayer";
import { OverlayLayer } from "./OverlayLayer";
import { useSvgConstructionAnimation } from "./useConstructionAnimation";

export interface MapSvgProps {
  /** The `<svg>` root; gestures attach here. */
  svgRef: React.RefObject<SVGSVGElement | null>;
  /** The pan/zoom `<g>`; gestures write its transform during a pan. */
  rootRef: React.RefObject<SVGGElement | null>;
  readOnly?: boolean;
}

export const viewportTransform = (viewport: {
  x: number;
  y: number;
  scale: number;
}): string =>
  `translate(${viewport.x}, ${viewport.y}) scale(${viewport.scale})`;

/** Margin around the screen, as a fraction of it, kept mounted so a pan
 *  gesture (which does not re-cull until it commits) shows no gaps. */
const CULL_MARGIN = 0.5;

/**
 * One `<svg>` with the viewport transform on a root group and the
 * document, overlay and draft layers inside it.
 */
export const MapSvg: React.FC<MapSvgProps> = ({
  svgRef,
  rootRef,
  readOnly
}) => {
  useSvgConstructionAnimation(svgRef);
  const viewport = useViewport();
  const { showOutside } = useEditorUi();
  const { editingZoneWidth, editingZoneHeight } = useDocumentMeta();
  const [size, setSize] = React.useState<{
    width: number;
    height: number;
  } | null>(null);
  const clipId = React.useId();

  React.useEffect(() => {
    const svg = svgRef.current;
    if (!svg) return;
    const update = () => {
      const rect = svg.getBoundingClientRect();
      setSize({ width: rect.width, height: rect.height });
    };
    update();
    const observer = new ResizeObserver(update);
    observer.observe(svg);
    return () => observer.disconnect();
  }, [svgRef]);

  const visibleArea = React.useMemo<Rect | null>(() => {
    // Not measured yet (or not laid out): draw everything rather than nothing.
    if (!size || size.width === 0 || size.height === 0) return null;
    const width = size.width / viewport.scale;
    const height = size.height / viewport.scale;
    return {
      x: -viewport.x / viewport.scale - width * CULL_MARGIN,
      y: -viewport.y / viewport.scale - height * CULL_MARGIN,
      width: width * (1 + CULL_MARGIN * 2),
      height: height * (1 + CULL_MARGIN * 2)
    };
  }, [size, viewport]);

  return (
    <svg
      ref={svgRef}
      // Explicit size attributes: without them the SVG falls back to the
      // 300x150 default and pointer events outside that box miss it.
      width="100%"
      height="100%"
      className="block h-full w-full touch-none select-none bg-neutral-100"
      xmlns="http://www.w3.org/2000/svg"
      data-map-editor-canvas
    >
      {/* Hover and selection feedback drawn by CSS, so no shape re-renders. */}
      <style>{`
        [data-hovered] .spot-body, [data-selected] .spot-body { stroke: #000000; stroke-width: 0.5; }
        [data-selected] .state-area-preview, [data-hovered] .state-area-preview { opacity: 1; }
      `}</style>
      <defs>
        <clipPath id={clipId}>
          <rect width={editingZoneWidth} height={editingZoneHeight} />
        </clipPath>
      </defs>
      <g
        ref={rootRef}
        transform={viewportTransform(viewport)}
        style={{ willChange: "transform" }}
      >
        <GridLayer patternId={`${clipId}-grid`} placement="under" />
        <g clipPath={showOutside ? undefined : `url(#${clipId})`}>
          <DocumentLayer visibleArea={visibleArea} />
        </g>
        <GridLayer patternId={`${clipId}-grid`} placement="over" />
        {readOnly ? null : (
          <>
            <AutoZoneSvg />
            <ZoneChecksSvg />
            <GuidePointSvg />
            <OverlayLayer />
            <DraftLayer />
          </>
        )}
      </g>
    </svg>
  );
};
